import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import RichTextEditor from "@/components/ui/rich-text-editor";
import SignatureModal from "@/components/emails/SignatureModal";
import WarningManager, { WarningManagerRef } from "@/components/emails/WarningManager";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import AttachmentUpload from "@/components/emails/AttachmentUpload";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { EmailTemplate } from "@/lib/types";

// Type definitions
interface Player {
  id: number;
  name: string;
  email: string;
  position?: string[] | null;
  specific_positions?: string[] | null;
  age?: number | null;
  height?: string | null;
  weight?: string | null;
  nationality?: string | null;
  current_club?: string | null;
  playing_level?: string | null;
  gpa?: string | null;
  hs_grad_year?: number | null;
  university_grad_year?: number | null;
  previous_clubs?: string[] | null;
  international_caps?: any[] | null;
  distinguishments?: string[] | null;
  highlight_videos?: string[] | null;
  attachments?: string[] | null;
  date_added?: string | null;
  email_content?: string | null;
  event?: string[];
}

// EmailTemplate is now imported from @/lib/types

interface EmailVariables {
  PlayerName: string;
  PlayerFirstName: string;
  PlayerLastName: string; 
  PlayerPosition: string;
  PlayerClub: string;
  PlayerLevel: string;
}

interface SendEmailData {
  to: string | string[];
  templateId?: number | string;
  subject: string;
  content: string;
  scheduledAt?: string;
  trackOpens: boolean;
  trackClicks: boolean;
  recipientId?: number;
  variables?: EmailVariables;
  selectedPlayerData?: Player | null;
  emailTags?: string[];
  selectedPlayerIds?: number[]; // Track which specific players were selected
  attachments?: AttachmentFile[]; // Add attachments to the data structure
}

interface AttachmentFile {
  id: string;
  file?: File; // Only for new uploads
  name: string;
  size: number;
  type: string;
  progress: number;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
  url?: string; // For preview or download
  serverId?: string; // For server-provided attachment ID
  // Server fields for existing attachments
  attachment_id?: number;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  upload_date?: string;
  storage_path?: string;
  isExisting?: boolean; // Flag to distinguish existing vs new files
}

interface Signature {
  name: string;
  title: string;
  organization: string;
  department: string;
  email: string;
  phone: string;
  other: string[]; 
}

interface MissingVariableIssue {
  playerName: string;
  playerId: number;
  missingVariables: {
    variable: keyof EmailVariables;
    label: string;
  }[];
}

// MissingVariableWarningProps is now handled by WarningManager

// Helper function to split player name
const splitPlayerName = (fullName: string): { firstName: string; lastName: string } => {
  if (!fullName || !fullName.trim()) {
    return { firstName: '', lastName: '' };
  }
  
  const nameParts = fullName.trim().split(' ');
  
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: '' };
  } else if (nameParts.length === 2) {
    return { firstName: nameParts[0], lastName: nameParts[1] };
  } else {
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');
    return { firstName, lastName };
  }
};

// Function to replace variables with actual values
export const replaceEmailVariables = (content: string, player: Player | null, variables?: EmailVariables): string => {
  if (!player && !variables) return content;

  const { firstName, lastName } = splitPlayerName(player?.name || '');
  
  const dynamicVariables: EmailVariables = variables || {
    PlayerName: player?.name || 'Player Name',
    PlayerFirstName: firstName || 'First Name',
    PlayerLastName: lastName || 'Last Name', 
    PlayerPosition: (player?.position && Array.isArray(player.position) && player.position.length > 0) ? player.position.join(', ') : 'Position',
    PlayerClub: player?.current_club || 'Current Club',
    PlayerLevel: player?.playing_level || 'Playing Level',
  };

  return content.replace(/\{\{([^}]+)\}\}/g, (match, variableName) => {
    const key = variableName.trim() as keyof EmailVariables;
    return dynamicVariables[key] || match;
  });
};

// EmailTagsInput component
const EmailTagsInput = ({ 
  tags, 
  onTagsChange, 
  placeholder = "Enter email addresses..." 
}: {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  placeholder?: string;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const addEmailTag = (email: string) => {
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail) return;
    
    if (!validateEmail(trimmedEmail)) {
      setInputError("Please enter a valid email address");
      return;
    }
    
    if (tags.includes(trimmedEmail)) {
      setInputError("Email already added");
      return;
    }
    
    onTagsChange([...tags, trimmedEmail]);
    setInputValue("");
    setInputError("");
  };

  const removeEmailTag = (emailToRemove: string) => {
    onTagsChange(tags.filter(email => email !== emailToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmailTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeEmailTag(tags[tags.length - 1]);
    } else if (e.key === ',') {
      e.preventDefault();
      addEmailTag(inputValue);
    }
    
    if (inputError) {
      setInputError("");
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emails = pastedText.split(/[,;\s\n]+/).filter(email => email.trim());
    
    const newTags = [...tags];
    emails.forEach(email => {
      const trimmedEmail = email.trim().toLowerCase();
      if (validateEmail(trimmedEmail) && !newTags.includes(trimmedEmail)) {
        newTags.push(trimmedEmail);
      }
    });
    
    onTagsChange(newTags);
    setInputValue("");
  };

  return (
    <div className="w-full">
      <div className="relative">
        <div className={`flex min-h-[2.5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
          inputError ? 'border-red-500' : ''
        }`}>
          <div className="flex flex-wrap gap-1 items-center flex-1 min-w-0">
            {tags.map((email, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors max-w-full"
              >
                <span className="truncate max-w-[200px]" title={email}>
                  {email}
                </span>
                <button
                  type="button"
                  onClick={() => removeEmailTag(email)}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-300 focus:outline-none flex-shrink-0"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              </span>
            ))}
            
            <input
              type="email"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={tags.length === 0 ? placeholder : ""}
              className="flex-1 min-w-[120px] border-none outline-none focus:ring-0 text-sm bg-transparent placeholder:text-muted-foreground"
            />
          </div>
        </div>
        
        <i className="fas fa-users absolute right-3 top-3 text-muted-foreground text-sm pointer-events-none"></i>
      </div>
      
      {inputError && (
        <p className="text-red-500 text-xs mt-1">{inputError}</p>
      )}
      
      <div className="text-muted-foreground text-xs mt-1 flex items-center justify-between">
        <span>
          Press Enter or comma to add emails.
        </span>
        {tags.length > 0 && (
          <span className="text-blue-600 font-medium">
            {tags.length} recipient{tags.length !== 1 ? 's' : ''} added
          </span>
        )}
      </div>
      
      {/* Show scrollable list when there are many emails */}
      {tags.length > 5 && (
        <div className="mt-2 p-2 bg-gray-50 rounded border max-h-20 overflow-y-auto">
          <p className="text-xs text-gray-600 mb-1">All recipients:</p>
          <div className="text-xs text-gray-700 space-y-0.5">
            {tags.map((email, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="truncate">{email}</span>
                <button
                  onClick={() => removeEmailTag(email)}
                  className="ml-2 text-red-500 hover:text-red-700"
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ComposeEmail() {
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  const [showEditSignature, setShowEditSignature] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [lastSentEmail, setLastSentEmail] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [shortlistInfo, setShortlistInfo] = useState<{
    name: string;
    id: number;
    playerCount: number;
  } | null>(null);
  
  // WarningManager ref for centralized warning handling
  const warningManagerRef = useRef<WarningManagerRef>(null);
  
  // Attachment state
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);

  // Clear attachments when component unmounts (navigate away) for Gmail-like behavior
  useEffect(() => {
    return () => {
      // Clear attachments on cleanup to ensure fresh state for next compose session
      setAttachments([]);
    };
  }, []);
  
  const [signature, setSignature] = useState<Signature>({
    name: "Your Name",
    title: "Your Title",
    organization: "Your University",
    department: "Your Department",
    email: "your.email@university.edu",
    phone: "Your Phone Number",
    other: [],
  });

  const [formData, setFormData] = useState<SendEmailData>({
    to: "",
    templateId: "",
    subject: "",
    content: "",
    scheduledAt: "",
    trackOpens: true,
    trackClicks: true,
    selectedPlayerData: null,
    emailTags: [],
    selectedPlayerIds: [], // Initialize empty array for selected player IDs
    attachments: [], // Initialize empty array for attachments
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const { data: players } = useQuery<Player[]>({
    queryKey: ["/api/players"],
  });

  const { data: templates } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates/"],
  });

  const { data: userProfile } = useQuery({
    queryKey: ["/api/auth/me"],
  });

  const { data: userPreferences } = useQuery({
    queryKey: ["/api/preferences/"],
  });
  const { data: sentEmails } = useQuery<any[]>({
    queryKey: ["/api/emails/sent-emails"],
  });

  // Track which emails have been sent to before
  const [previouslySentEmails, setPreviouslySentEmails] = useState<Set<string>>(new Set());
  
  // Effect to populate previously sent emails
  useEffect(() => {
    if (sentEmails && sentEmails.length > 0) {
      console.log('[SENT EMAILS] Fetched sent emails:', sentEmails);
      const emailSet = new Set<string>();
      sentEmails.forEach(email => {
          if (email.recipient && typeof email.recipient === 'string') {
          // Extract clean email address and add to set
          const cleanEmail = email.recipient.toLowerCase().trim();
          emailSet.add(cleanEmail);
        }
      });
      console.log('[SENT EMAILS] Populated previouslySentEmails Set with:', Array.from(emailSet));
        setPreviouslySentEmails(new Set(emailSet));
    }
  }, [sentEmails]);
  
  // Effect to match emails to player IDs when players load (for shortlist feature)
  useEffect(() => {
    if (players && formData.emailTags && formData.emailTags.length > 0 && 
        (!formData.selectedPlayerIds || formData.selectedPlayerIds.length === 0)) {
      // We have emails but no player IDs - likely loaded from shortlist
      const matchedPlayerIds: number[] = [];
      
      formData.emailTags.forEach(email => {
        const matchingPlayer = players.find(p => 
          p.email && p.email.toLowerCase().trim() === email.toLowerCase()
        );
        if (matchingPlayer && !matchedPlayerIds.includes(matchingPlayer.id)) {
          matchedPlayerIds.push(matchingPlayer.id);
        }
      });
      
      if (matchedPlayerIds.length > 0) {
        setFormData(prev => ({
          ...prev,
          selectedPlayerIds: matchedPlayerIds
        }));
      }
    }
  }, [players, formData.emailTags]);

  // Removed duplicate fetch - previouslySentEmails is now populated from the sentEmails query

  // Load email data from localStorage when component mounts
  useEffect(() => {
    const loadEmailDataFromStorage = () => {
      try {
        const storedEmailData = localStorage.getItem('composeEmailData');
        if (storedEmailData) {
          const emailData = JSON.parse(storedEmailData);
          
          // Check if the data is recent (within last 5 minutes to avoid stale data)
          const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
          if (emailData.timestamp && emailData.timestamp > fiveMinutesAgo) {
            // Auto-populate the email addresses in both emailTags and to fields
            // Note: When loading from shortlist, we don't have player IDs yet
            setFormData(prev => ({
              ...prev,
              emailTags: emailData.emails || [],
              selectedPlayerIds: [], // Will be populated when players are loaded
              to: (emailData.emails || []).join(', ')
            }));
            
            // Set shortlist info for display
            if (emailData.shortlistName) {
              setShortlistInfo({
                name: emailData.shortlistName,
                id: emailData.shortlistId,
                playerCount: emailData.playerCount || emailData.emails?.length || 0
              });
            }
            
            // Show success message
            toast({
              title: "Emails loaded from shortlist",
              description: `Added ${emailData.emails?.length || 0} email addresses from "${emailData.shortlistName}".`,
            });
            
            // Clear the stored data to prevent reloading on refresh
            localStorage.removeItem('composeEmailData');
            
            return true; // Successfully loaded data
          } else {
            // Remove stale data
            localStorage.removeItem('composeEmailData');
          }
        }
      } catch (error) {
        console.error('Failed to load stored email data:', error);
        localStorage.removeItem('composeEmailData');
      }
      return false; // No data loaded
    };

    // Try to load immediately
    const loaded = loadEmailDataFromStorage();
    
    // If no data was loaded, set up a listener for when the user navigates back to this page
    if (!loaded) {
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'composeEmailData' && e.newValue) {
          loadEmailDataFromStorage();
        }
      };
      
      window.addEventListener('storage', handleStorageChange);
      
      // Also check periodically in case the storage event doesn't fire
      const intervalId = setInterval(() => {
        const hasData = localStorage.getItem('composeEmailData');
        if (hasData) {
          loadEmailDataFromStorage();
          clearInterval(intervalId);
        }
      }, 1000);
      
      // Cleanup
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        clearInterval(intervalId);
      };
    }
  }, [toast]);

  useEffect(() => {
    if (userPreferences && (userPreferences as any)?.email_signature) {
      try {
        const parsedSignature = JSON.parse((userPreferences as any).email_signature);
        setSignature({
          name: parsedSignature.name || "Your Name",
          title: parsedSignature.title || "Your Title",
          organization: parsedSignature.organization || "Your University",
          department: parsedSignature.department || "Your Department",
          email: parsedSignature.email || "your.email@university.edu",
          phone: parsedSignature.phone || "Your Phone Number",
          other: Array.isArray(parsedSignature.other) ? parsedSignature.other : [],
        });
      } catch (error) {
        console.error("Failed to parse email signature:", error);
      }
    } else if (userProfile) {
      setSignature({
        name: (userProfile as any)?.name || "Your Name",
        title: "Your Title", 
        organization: (userProfile as any)?.school || "Your University", 
        department: "Your Department", 
        email: (userProfile as any)?.email || "your.email@university.edu",
        phone: "Your Phone Number",
        other: [],
      });
    }
  }, [userProfile, userPreferences]);

  const filteredPlayers = useMemo(() => {
    if (!players) return [];
    
    if (!searchTerm.trim()) {
      return players;
    }
    
    const searchQuery = searchTerm.toLowerCase().trim();
    
    return players.filter(player => {
      // Search by name
      const nameMatch = player.name?.toLowerCase().includes(searchQuery);
      
      // Search by email
      const emailMatch = player.email?.toLowerCase().includes(searchQuery);
      
      // Search by position (general positions)
      const positionMatch = player.position && Array.isArray(player.position) 
        ? player.position.some(pos => pos.toLowerCase().includes(searchQuery))
        : false;
      
      // Search by specific positions
      const specificPositionMatch = player.specific_positions && Array.isArray(player.specific_positions)
        ? player.specific_positions.some(pos => pos.toLowerCase().includes(searchQuery))
        : false;
      
      // Search by current club
      const clubMatch = player.current_club?.toLowerCase().includes(searchQuery);
      
      // Search by nationality
      const nationalityMatch = player.nationality?.toLowerCase().includes(searchQuery);
      
      // Search by playing level
      const levelMatch = player.playing_level?.toLowerCase().includes(searchQuery);
      
      // Search by age (convert to string for searching)
      const ageMatch = player.age?.toString().includes(searchQuery);
      
      // Search by GPA
      const gpaMatch = player.gpa?.toLowerCase().includes(searchQuery);
      
      // Search by graduation years
      const hsGradMatch = player.hs_grad_year?.toString().includes(searchQuery);
      const uniGradMatch = player.university_grad_year?.toString().includes(searchQuery);
      
      // Search by event (array of strings)
      const eventMatch = Array.isArray(player.event)
        ? player.event.some(ev => ev.toLowerCase().includes(searchQuery))
        : false;
      
      // Return true if any field matches
      return nameMatch || emailMatch || positionMatch || specificPositionMatch || 
            clubMatch || nationalityMatch || levelMatch || eventMatch || ageMatch || 
            gpaMatch || hsGradMatch || uniGradMatch;
      });
    }, [players, searchTerm]);

  // getPreviewContent removed - now handled by WarningManager

  // Warning check functions for WarningManager
  const checkGraduationWarnings = async (emailTags: string[]) => {
    const response = await apiRequest("POST", "/api/email-sending/check-graduation-warnings", {
      emailTags
    });
    return response.json();
  };

  const checkDuplicateEmails = async (emailTags: string[]) => {
    const response = await apiRequest("POST", "/api/email-sending/check-duplicate-emails", {
      emailTags
    });
    return response.json();
  };

  const sendEmailMutation = useMutation({
    mutationFn: async (data: SendEmailData) => {
      console.log('=== SENDING EMAIL ===');
      console.log('Email data being sent:', data);
      console.log('Attachments in email data:', data.attachments);
      
      const response = await apiRequest("POST", "/api/email-sending/send", data);
      
      console.log('Email send response:', response);
      
      // Handle HTTP errors before parsing JSON
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || `HTTP Error ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      return response.json();
    },
    onSuccess: (response) => {
      console.log('Email send success:', response);
      
      // Validate actual success from backend response
      const overallStatus = response?.overall_status;
      const successfulSends = response?.successful_sends || 0;
      const totalRecipients = response?.total_recipients || 0;
      const failedSends = response?.failed_sends || 0;
      
      // Only show success if emails were actually sent
      if (overallStatus === "sent" && successfulSends > 0) {
        setEmailSent(true);
        const playerCount = formData.selectedPlayerIds?.length || 0;
        const emailCount = formData.emailTags?.length || 0;
        const displayText = playerCount > 0 ? 
          `${playerCount} player${playerCount !== 1 ? 's' : ''}` +
          (playerCount !== emailCount ? ` (${emailCount} email${emailCount !== 1 ? 's' : ''})` : '') :
          `${emailCount} recipient${emailCount !== 1 ? 's' : ''}`;
        
        setLastSentEmail(displayText);
        
        toast({
          title: "✅ Email sent successfully!",
          description: `Your email was delivered to ${displayText}`,
        });
      } else if (overallStatus === "partial" && successfulSends > 0) {
        // Partial success - some emails sent, some failed
        const displayText = `${successfulSends} of ${totalRecipients} recipients`;
        
        toast({
          title: "⚠️ Partially sent",
          description: `Email delivered to ${displayText}. ${failedSends} failed.`,
          variant: "default",
        });
      } else {
        // No emails were actually sent
        throw new Error("No emails were sent successfully");
      }
      
      setTimeout(() => {
        setEmailSent(false);
        setLastSentEmail("");
      }, 5000);
      
      // Update previously sent emails set
      if (formData.emailTags && formData.emailTags.length > 0) {
        setPreviouslySentEmails(prev => {
          const newSet = new Set(prev);
          formData.emailTags?.forEach(email => {
            newSet.add(email.toLowerCase().trim());
          });
          return newSet;
        });
      }
      
      // Email sent successfully - warning states handled by WarningManager
      
      queryClient.invalidateQueries({ queryKey: ["/api/emails/"] });
      queryClient.invalidateQueries({ queryKey: ["/api/emails/sent-emails"] });
    },
    onError: (error: Error) => {
      setShowConfirmSend(false);
      
      // Check if this is a session expiration error
      const errorMessage = error.message || "";
      const isSessionExpired = errorMessage.includes("Session Has Expired") || 
                               errorMessage.includes("Session Expired") ||
                               errorMessage.includes("Please log out and") ||
                               (errorMessage.includes("401") && errorMessage.includes("authentication"));
      
      if (isSessionExpired) {
        toast({
          title: "❌ Session Expired",
          description: "Your Session Has Expired. Please log out and log in again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "❌ Failed to send email",
          description: errorMessage || "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.emailTags || formData.emailTags.length === 0) {
      newErrors.to = "At least one recipient is required";
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    if (!formData.content.trim()) {
      newErrors.content = "Message is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    setShowConfirmSend(true);
  };

  const confirmSend = () => {
    const playerForVariables = formData.selectedPlayerData;
    
    // Debug logging for attachments
    console.log('=== EMAIL SENDING DEBUG ===');
    console.log('Current attachments state:', attachments);
    console.log('Attachments being sent:', attachments.map(att => ({
      id: att.id,
      name: att.name,
      status: att.status,
      attachment_id: att.attachment_id,
      serverId: att.serverId,
      isExisting: att.isExisting
    })));
    
    const emailData: SendEmailData = {
      to: formData.emailTags || [],
      templateId: formData.templateId,
      subject: formData.subject,
      content: formData.content,
      scheduledAt: formData.scheduledAt,
      trackOpens: formData.trackOpens,
      trackClicks: formData.trackClicks,
      recipientId: playerForVariables?.id,
      emailTags: formData.emailTags,
      selectedPlayerData: playerForVariables,
      attachments: attachments,
    };
    
    console.log('Email data being sent to backend:', emailData);
    console.log('=== END EMAIL SENDING DEBUG ===');
    
    // Store email data for later sending
    // Use WarningManager to handle all warnings in the correct order
    if (warningManagerRef.current) {
      warningManagerRef.current.processWarnings(emailData);
    } else {
      // Fallback to direct send if WarningManager is not available
      sendEmailMutation.mutate(emailData);
    }
  };

  const cancelSend = () => {
    setShowConfirmSend(false);
  };

  // Warning handlers are now managed by WarningManager

  const handleTemplateSelect = async (templateId: string) => {
    const template = templates?.find((t) => t.id === parseInt(templateId));
    if (template) {
      setFormData(prev => ({
        ...prev,
        templateId,
        subject: template.subject,
        content: template.content,
      }));
      
      // Load template attachments
      try {
        const response = await apiRequest("GET", `/api/attachments/template/${templateId}`);
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.attachments && data.attachments.length > 0) {
            // Convert template attachments to AttachmentFile format
            const templateAttachments: AttachmentFile[] = data.attachments.map((attachment: any) => ({
              id: `template-${attachment.id}`,
              name: attachment.file_name,
              size: attachment.file_size,
              type: attachment.file_type,
              progress: 100,
              status: 'completed' as const,
              attachment_id: attachment.id,
              file_name: attachment.file_name,
              file_size: attachment.file_size,
              file_type: attachment.file_type,
              upload_date: attachment.upload_date,
              isExisting: true,
              serverId: attachment.id.toString()
            }));
            
            // Add template attachments to current attachments (Gmail-like behavior: template attachments become part of this compose session)
            setAttachments(prevAttachments => {
              // Remove any existing template attachments first
              const nonTemplateAttachments = prevAttachments.filter(att => !att.id.startsWith('template-'));
              return [...nonTemplateAttachments, ...templateAttachments];
            });
            
            toast({
              title: "Template loaded",
              description: `Template "${template.name}" loaded with ${templateAttachments.length} attachment${templateAttachments.length !== 1 ? 's' : ''}`,
            });
          }
        }
      } catch (error) {
        console.error('Error loading template attachments:', error);
        // Don't show error toast - just continue without attachments
      }
    } else {
      setFormData(prev => ({
        ...prev,
        templateId,
      }));
    }
  };

  // handlePlayerSelect removed - not used

  const handlePlayerClick = (player: Player) => {
    setSelectedPlayer(player);
    setFormData(prev => ({
      ...prev,
      selectedPlayerData: player,
    }));
    
    if (showAllPlayers) {
      setShowAllPlayers(false);
      setSearchTerm("");
    }
  };

  // Add state to track focused field (subject or content)
  const [focusedField, setFocusedField] = useState<'subject' | 'content'>('content');

  const richTextEditorRef = useRef<any>(null);

  // Update insertVariable to insert at cursor position
  const insertVariable = (variable: keyof EmailVariables) => {
    if (focusedField === 'subject') {
      const input = document.getElementById('subject') as HTMLInputElement;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newValue = formData.subject.slice(0, start) + `{{${variable}}}` + formData.subject.slice(end);
        setFormData(prev => ({ ...prev, subject: newValue }));
        setTimeout(() => {
          // Don't automatically focus - let user control where they want to insert
          input.setSelectionRange(start + `{{${variable}}}`.length, start + `{{${variable}}}`.length);
        }, 0);
      }
    } else {
      // Use RichTextEditor ref for cursor-aware insertion
      if (richTextEditorRef.current && typeof richTextEditorRef.current.insertVariable === 'function') {
        richTextEditorRef.current.insertVariable(variable);
      } else {
        setFormData(prev => ({ ...prev, content: prev.content + `{{${variable}}}` }));
      }
    }
  };

  const generateSignatureText = (sig: Signature): string => {
    const lines: string[] = [];
    lines.push("\n\n---");
    if (sig.name) lines.push(sig.name);
    if (sig.title) lines.push(sig.title);
    if (sig.organization) lines.push(sig.organization);
    if (sig.department) lines.push(sig.department);
    if (sig.email) lines.push(sig.email);
    if (sig.phone) lines.push(sig.phone);
    sig.other.filter(field => field.trim()).forEach(field => {
      lines.push(field);
    });
    return lines.join('\n');
  };

  const addSignatureToContent = () => {
    const signatureText = generateSignatureText(signature);
    setFormData(prev => ({
      ...prev,
      content: prev.content + signatureText,
    }));
    
    toast({
      title: "Signature added",
      description: "Your email signature has been added to the message.",
    });
  };

  const handleSignatureUpdate = (updatedSignature: Signature) => {
    setSignature(updatedSignature);
    queryClient.invalidateQueries({ queryKey: ["/api/preferences/"] });
  };

  const isFormReady = () => {
    const hasValidRecipients = formData.emailTags && formData.emailTags.length > 0 && 
                               formData.emailTags.every(email => email.trim().length > 0);
    const hasValidSubject = formData.subject && formData.subject.trim().length > 0;
    const hasValidContent = formData.content && formData.content.trim().length > 0;
    const hasValidAttachments = attachments.length === 0 || attachments.every(att => att.status === 'completed');
    
    // Additional check: if there are uploading attachments, form is not ready
    const hasUploadingAttachments = attachments.some(att => att.status === 'uploading');
    
    return hasValidRecipients && 
           hasValidSubject && 
           hasValidContent && 
           hasValidAttachments &&
           !hasUploadingAttachments;
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const addPlayerEmail = (player: Player) => {
    if (!player.email) {
      toast({
        title: "No email found",
        description: `${player.name} doesn't have an email address.`,
        variant: "destructive",
      });
      return;
    }

    const emailAddress = player.email.toLowerCase().trim();
    const currentTags = formData.emailTags || [];
    const currentPlayerIds = formData.selectedPlayerIds || [];
    
    // Check if this specific player is already selected
    if (currentPlayerIds.includes(player.id)) {
      toast({
        title: "Player already selected",
        description: `${player.name} is already in the recipient list.`,
        variant: "destructive",
      });
      return;
    }
    
    // Check if multiple players have the same email
    const playersWithSameEmail = players?.filter(p => 
      p.email && p.email.toLowerCase().trim() === emailAddress
    ) || [];
    
    if (playersWithSameEmail.length > 1) {
      const alreadySelectedWithSameEmail = playersWithSameEmail.filter(p => 
        currentPlayerIds.includes(p.id)
      );
      
      if (alreadySelectedWithSameEmail.length > 0) {
        toast({
          title: "Email already in use",
          description: `This email is already selected for ${alreadySelectedWithSameEmail[0].name}. The email will only be sent once.`,
          variant: "default",
        });
      }
    }

    // Add email only if it's not already in the list
    const newTags = currentTags.includes(emailAddress) 
      ? currentTags 
      : [...currentTags, emailAddress];
    
    // Always add the player ID
    const newPlayerIds = [...currentPlayerIds, player.id];
    
    setFormData(prev => ({
      ...prev,
      emailTags: newTags,
      selectedPlayerIds: newPlayerIds,
      to: newTags.join(', ')
    }));

    toast({
      title: "Player added",
      description: `Added ${player.name} to recipients.`,
    });
  };

  const removePlayerEmail = (player: Player) => {
    if (!player.email) return;

    const currentTags = formData.emailTags || [];
    const currentPlayerIds = formData.selectedPlayerIds || [];
    const emailToRemove = player.email.toLowerCase().trim();
    
    // Remove this player's ID
    const newPlayerIds = currentPlayerIds.filter(id => id !== player.id);
    
    // Check if any other selected players have the same email
    const otherPlayersWithSameEmail = players?.filter(p => 
      p.email && 
      p.email.toLowerCase().trim() === emailToRemove && 
      p.id !== player.id &&
      newPlayerIds.includes(p.id)
    ) || [];
    
    // Only remove the email if no other selected players are using it
    const newTags = otherPlayersWithSameEmail.length > 0
      ? currentTags
      : currentTags.filter(email => email !== emailToRemove);
    
    setFormData(prev => ({
      ...prev,
      emailTags: newTags,
      selectedPlayerIds: newPlayerIds,
      to: newTags.join(', ')
    }));

    toast({
      title: "Player removed",
      description: `Removed ${player.name} from recipients.`,
    });
  };

  const selectAllPlayers = () => {
    const playersWithEmails = filteredPlayers.filter(player => player.email);
    const currentTags = formData.emailTags || [];
    const currentPlayerIds = formData.selectedPlayerIds || [];
    
    // Find players not yet selected
    const playersToAdd = playersWithEmails.filter(player => 
      !currentPlayerIds.includes(player.id)
    );

    if (playersToAdd.length === 0) {
      toast({
        title: "All players already added",
        description: "All players with emails are already in the recipient list.",
      });
      return;
    }

    // Collect unique emails from players to add
    const emailsToAdd = new Set<string>();
    playersToAdd.forEach(player => {
      emailsToAdd.add(player.email.toLowerCase().trim());
    });
    
    // Add new emails to existing tags
    const allTags = [...currentTags];
    emailsToAdd.forEach(email => {
      if (!allTags.includes(email)) {
        allTags.push(email);
      }
    });
    
    // Add all player IDs
    const allPlayerIds = [...currentPlayerIds, ...playersToAdd.map(p => p.id)];
    
    setFormData(prev => ({
      ...prev,
      emailTags: allTags,
      selectedPlayerIds: allPlayerIds,
      to: allTags.join(', ')
    }));

    toast({
      title: "All players added",
      description: `Added ${playersToAdd.length} player${playersToAdd.length !== 1 ? 's' : ''} to recipients.`,
    });
  };

  const selectNewRecipients = () => {
    const playersWithEmails = filteredPlayers.filter(player => player.email);
    const currentPlayerIds = formData.selectedPlayerIds || [];
    const currentTags = formData.emailTags || [];
    
    // Filter for players who haven't been sent emails before and aren't already selected
    const newPlayers = playersWithEmails.filter(player => {
      const playerEmail = player.email.toLowerCase().trim();
      return !previouslySentEmails.has(playerEmail) && !currentPlayerIds.includes(player.id);
    });

    if (newPlayers.length === 0) {
      // Check if there are any new recipients at all
      const hasNewRecipientsTotal = playersWithEmails.some(player => 
        !previouslySentEmails.has(player.email.toLowerCase().trim())
      );
      
      if (!hasNewRecipientsTotal) {
        toast({
          title: "No new recipients found",
          description: "All players have been emailed before.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "New recipients already added",
          description: "All new recipients are already in the list.",
        });
      }
      return;
    }

    // Collect unique emails from new players
    const emailsToAdd = new Set<string>();
    newPlayers.forEach(player => {
      emailsToAdd.add(player.email.toLowerCase().trim());
    });
    
    // Add new emails to existing tags
    const allTags = [...currentTags];
    emailsToAdd.forEach(email => {
      if (!allTags.includes(email)) {
        allTags.push(email);
      }
    });
    
    // Add all new player IDs
    const allPlayerIds = [...currentPlayerIds, ...newPlayers.map(p => p.id)];
    
    setFormData(prev => ({
      ...prev,
      emailTags: allTags,
      selectedPlayerIds: allPlayerIds,
      to: allTags.join(', ')
    }));

    toast({
      title: "New recipients added",
      description: `Added ${newPlayers.length} new recipient${newPlayers.length !== 1 ? 's' : ''} who haven't been emailed before.`,
    });
  };

  const clearAllPlayerEmails = () => {
    setFormData(prev => ({
      ...prev,
      emailTags: [],
      selectedPlayerIds: [],
      to: ''
    }));

    toast({
      title: "All recipients cleared",
      description: "Removed all recipients from the list.",
    });
  };

  const isPlayerEmailAdded = (player: Player) => {
    if (!player.email) return false;
    // Check if this specific player ID is in the selected list
    return (formData.selectedPlayerIds || []).includes(player.id);
  };

  const getRecipientPreviews = () => {
    if (!formData.emailTags || formData.emailTags.length === 0 || !formData.content.includes('{{')) {
      return [];
    }

    return formData.emailTags.map(email => {
      // Find player with matching email
      const matchingPlayer = players?.find(player => 
        player.email && player.email.toLowerCase() === email.toLowerCase()
      );

      if (matchingPlayer) {
        // Generate preview with this player's data
        const personalizedContent = replaceEmailVariables(formData.content, matchingPlayer);
        const personalizedSubject = replaceEmailVariables(formData.subject, matchingPlayer);
        
        return {
          email,
          playerName: matchingPlayer.name,
          hasPlayer: true,
          previewSubject: personalizedSubject,
          previewContent: personalizedContent.substring(0, 150) + (personalizedContent.length > 150 ? '...' : '')
        };
      } else {
        // No player found, will send generic email
        return {
          email,
          playerName: null,
          hasPlayer: false,
          previewSubject: formData.subject,
          previewContent: formData.content.substring(0, 150) + (formData.content.length > 150 ? '...' : '')
        };
      }
    });
  };

  // Add function to clear shortlist info
  const clearShortlistInfo = () => {
    setShortlistInfo(null);
    toast({
      title: "Shortlist info cleared",
      description: "Removed shortlist reference from this email.",
    });
  };

  // Warning handlers are now managed by WarningManager

  // Helper function to get variable label
  const getVariableLabel = (variable: keyof EmailVariables): string => {
    const labels: Record<keyof EmailVariables, string> = {
      PlayerName: "Player Name (Full)",
      PlayerFirstName: "First Name", 
      PlayerLastName: "Last Name",
      PlayerPosition: "General Position",
      PlayerClub: "Current Club",
      PlayerLevel: "Playing Level"
    };
    return labels[variable];
  };

  // Function to check if a variable has a meaningful value for a player
  const hasVariableValue = (player: Player, variable: keyof EmailVariables): boolean => {
    const { firstName, lastName } = splitPlayerName(player?.name || '');
    
    switch (variable) {
      case 'PlayerName':
        return !!(player?.name && player.name.trim());
      case 'PlayerFirstName':
        return !!(firstName && firstName.trim());
      case 'PlayerLastName':
        return !!(lastName && lastName.trim());
      case 'PlayerPosition':
        return !!(player?.position && Array.isArray(player.position) && player.position.length > 0 && player.position.some(p => p.trim()));
      case 'PlayerClub':
        return !!(player?.current_club && player.current_club.trim());
      case 'PlayerLevel':
        return !!(player?.playing_level && player.playing_level.trim());
      default:
        return false;
    }
  };

  // Function to extract variables used in content
  const extractVariablesFromContent = (content: string): (keyof EmailVariables)[] => {
    const variableMatches = content.match(/\{\{([^}]+)\}\}/g) || [];
    const extractedVariables: (keyof EmailVariables)[] = [];
    
    variableMatches.forEach(match => {
      const variableName = match.replace(/[{}]/g, '').trim() as keyof EmailVariables;
      if (['PlayerName', 'PlayerFirstName', 'PlayerLastName', 'PlayerPosition', 'PlayerClub', 'PlayerLevel'].includes(variableName)) {
        if (!extractedVariables.includes(variableName)) {
          extractedVariables.push(variableName);
        }
      }
    });
    
    return extractedVariables;
  };

  // Function to validate variables for players
  const validateVariablesForPlayers = (content: string, subject: string, players: Player[]): MissingVariableIssue[] => {
    const allContent = `${subject} ${content}`;
    const usedVariables = extractVariablesFromContent(allContent);
    
    if (usedVariables.length === 0) {
      return [];
    }
    
    const issues: MissingVariableIssue[] = [];
    
    players.forEach(player => {
      const missingVariables: { variable: keyof EmailVariables; label: string; }[] = [];
      
      usedVariables.forEach(variable => {
        if (!hasVariableValue(player, variable)) {
          missingVariables.push({
            variable,
            label: getVariableLabel(variable)
          });
        }
      });
      
      if (missingVariables.length > 0) {
        issues.push({
          playerName: player.name || 'Unknown Player',
          playerId: player.id,
          missingVariables
        });
      }
    });
    
    return issues;
  };

  // MissingVariableWarningModal is now handled by WarningManager

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-neutral-200 w-full">
      {emailSent && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm">✓</span>
            </div>
            <div>
              <p className="text-green-800 font-medium">Email sent successfully!</p>
              <p className="text-green-600 text-sm">Your message was delivered to {lastSentEmail}</p>
            </div>
          </div>
        </div>
      )}

      {/* Shortlist Info Banner */}
      {shortlistInfo && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <i className="fas fa-star text-white text-sm"></i>
              </div>
              <div>
                <p className="text-blue-800 font-medium">Emailing Shortlist: {shortlistInfo.name}</p>
                <p className="text-blue-600 text-sm">
                  {shortlistInfo.playerCount} players • {formData.emailTags?.length || 0} email addresses loaded
                </p>
              </div>
            </div>
            <button
              onClick={clearShortlistInfo}
              className="text-blue-500 hover:text-blue-700 p-1"
              title="Clear shortlist reference"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="min-w-0">
            <Label htmlFor="to">To</Label>
            <div className="relative">
              <EmailTagsInput
                tags={formData.emailTags || []}
                onTagsChange={(tags) => setFormData(prev => ({ 
                  ...prev, 
                  emailTags: tags,
                  to: tags.join(', ')
                }))}
                placeholder="Enter email addresses and press Enter..."
              />
            </div>
            {errors.to && <p className="text-red-500 text-sm mt-1">{errors.to}</p>}
            
            {/* Show helpful message if emails were auto-loaded */}
            {shortlistInfo && formData.emailTags && formData.emailTags.length > 0 && (
              <p className="text-blue-600 text-xs mt-1">
                <i className="fas fa-info-circle mr-1"></i>
                {formData.emailTags.length} email addresses loaded from shortlist. You can add more or remove any as needed.
              </p>
            )}
          </div>

          <div className="min-w-0">
            <Label htmlFor="template">Template</Label>
            <Select
              value={formData.templateId?.toString() || ""}
              onValueChange={(value) => handleTemplateSelect(value)}
            >
              <SelectTrigger id="template">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates?.filter(template => template.id).map((template) => (
                  <SelectItem key={template.id} value={template.id!.toString()}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="subject">Subject</Label>
          <div className="flex gap-2">
            <Input
              id="subject"
              placeholder="Enter email subject"
              value={formData.subject}
              onChange={(e) => handleInputChange("subject", e.target.value)}
              onFocus={() => setFocusedField('subject')}
              className={errors.subject ? "border-red-500 flex-1" : "flex-1"}
            />
          </div>
          {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="content">Message</Label>
          </div>
          <div className="w-full" onClick={() => setFocusedField('content')}>
            <RichTextEditor
              ref={richTextEditorRef}
              content={formData.content}
              onChange={(value) => handleInputChange("content", value)}
              placeholder={shortlistInfo ? 
                `Write your message to ${shortlistInfo.name} players here...` : 
                "Write your message here..."
              }
              selectedPlayer={formData.selectedPlayerData || null}
              variables={formData.selectedPlayerData ? {
                PlayerName: formData.selectedPlayerData.name,
                PlayerFirstName: splitPlayerName(formData.selectedPlayerData.name || '').firstName,
                PlayerLastName: splitPlayerName(formData.selectedPlayerData.name || '').lastName,
                PlayerPosition: (formData.selectedPlayerData.position && Array.isArray(formData.selectedPlayerData.position)) ? formData.selectedPlayerData.position.join(', ') : '',
                PlayerClub: formData.selectedPlayerData.current_club || '',
                PlayerLevel: formData.selectedPlayerData.playing_level || '',
              } : undefined}
            />
          </div>
          {errors.content && <p className="text-red-500 text-sm mt-1">{errors.content}</p>}
        </div>

        {/* Attachments Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Attachments</Label>
            <span className="text-xs text-gray-500">
              Optional files to include with your email
            </span>
          </div>
          
          {/* Upload status indicator */}
          {attachments.some(att => att.status === 'uploading') && (
            <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-blue-700">
                  Uploading attachments... Please wait before sending.
                </span>
              </div>
            </div>
          )}
          
          <AttachmentUpload
            attachments={attachments}
            onAttachmentsChange={(newAttachments) => {
              setAttachments(newAttachments);
              setFormData(prev => ({ ...prev, attachments: newAttachments }));
            }}
            maxFileSize={25 * 1024 * 1024} // 25MB
            maxTotalSize={100 * 1024 * 1024} // 100MB
            maxFiles={10}
            disabled={sendEmailMutation.isPending}
            showExisting={false}
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addSignatureToContent}
              className="text-xs px-3 py-1 h-7"
            >
              <i className="fas fa-signature mr-1"></i>
              Add Signature
            </Button>
            
            <Button 
              type="button" 
              variant="outline"
            >
              Save Draft
            </Button>
            
            {!showConfirmSend ? (
              <Button
                type="submit"
                disabled={sendEmailMutation.isPending || !isFormReady()}
                variant="default"
                className="w-full"
              >
                {sendEmailMutation.isPending ? "Sending..." : (
                  `Send Email${attachments.length > 0 ? ` (${attachments.length} attachment${attachments.length !== 1 ? 's' : ''})` : ''}`
                )}
              </Button>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelSend}
                  disabled={sendEmailMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={confirmSend}
                  disabled={sendEmailMutation.isPending}
                  variant="default"
                >
                  {sendEmailMutation.isPending ? (
                    <span className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Sending...</span>
                    </span>
                  ) : (
                    `✓ Confirm Send to ${formData.selectedPlayerIds?.length || 0} Player${(formData.selectedPlayerIds?.length || 0) !== 1 ? 's' : ''}`
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        {showConfirmSend && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm">?</span>
            </div>
            <div className="flex-1">
              <h4 className="text-blue-900 font-medium">Confirm Email Send</h4>
              <p className="text-blue-700 text-sm mt-1">
                Send to <strong>{formData.selectedPlayerIds?.length || 0} player{(formData.selectedPlayerIds?.length || 0) !== 1 ? 's' : ''}</strong>
                {formData.emailTags && formData.selectedPlayerIds && formData.selectedPlayerIds.length !== formData.emailTags.length && (
                  <span className="text-blue-600">
                    {' '}({formData.emailTags.length} unique email{formData.emailTags.length !== 1 ? 's' : ''})
                  </span>
                )}?
              </p>
              
              {/* Show personalization status */}
              {formData.content.includes('{{') && (
                <div className="mt-3">
                  <p className="text-xs text-gray-600 mb-2">Personalization Preview:</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {getRecipientPreviews().map((preview, index) => (
                      <div key={index} className="p-2 bg-white rounded border text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-800">{preview.email}</span>
                          {preview.hasPlayer ? (
                            <span className="text-green-600 text-xs bg-green-100 px-2 py-0.5 rounded">
                              ✓ {preview.playerName}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-xs bg-gray-100 px-2 py-0.5 rounded">
                              Generic
                            </span>
                          )}
                        </div>
                        <div className="text-gray-600">
                          <p className="font-medium">Subject: "{preview.previewSubject}"</p>
                          <p className="mt-1 text-gray-500">{preview.previewContent}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Summary stats */}
                  <div className="mt-2 flex gap-4 text-xs">
                    <span className="text-green-600">
                      <strong>{getRecipientPreviews().filter(p => p.hasPlayer).length}</strong> personalized
                    </span>
                    <span className="text-gray-500">
                      <strong>{getRecipientPreviews().filter(p => !p.hasPlayer).length}</strong> generic
                    </span>
                  </div>
                </div>
              )}
              
              {/* Original recipients display for non-variable emails */}
              {!formData.content.includes('{{') && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600 mb-1">Recipients:</p>
                  <div className="flex flex-wrap gap-1">
                    {formData.emailTags?.slice(0, 5).map((email, index) => (
                      <span key={index} className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {email}
                      </span>
                    ))}
                    {(formData.emailTags?.length || 0) > 5 && (
                      <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        +{(formData.emailTags?.length || 0) - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </form>

      <div className="w-full mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {/* Enhanced Player Selection */}
          <div className="bg-white p-3 lg:p-4 rounded-lg shadow-sm border border-neutral-200 min-h-[150px] flex flex-col">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <i className="fas fa-users text-sm"></i>
                <span className="text-sm lg:text-base">Select Players</span>
              </div>
              <span className="text-xs text-gray-500">
                {players?.length || 0} total
              </span>
            </h3>

            {/* Action Buttons */}
            <div className="mb-3 space-y-2">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={selectAllPlayers}
                  className="text-xs px-2 py-1 h-7 flex-1"
                  disabled={!filteredPlayers.some(player => player.email)}
                >
                  <i className="fas fa-check text-xs mr-1"></i>
                  Add All
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={selectNewRecipients}
                  className="text-xs px-2 py-1 h-7 flex-1"
                  disabled={!filteredPlayers.some(player => player.email)}
                >
                  <i className="fas fa-user-plus text-xs mr-1"></i>
                  Add New
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearAllPlayerEmails}
                  className="text-xs px-2 py-1 h-7 flex-1"
                  disabled={!formData.emailTags || formData.emailTags.length === 0}
                >
                  <i className="fas fa-times text-xs mr-1"></i>
                  Clear
                </Button>
              </div>
            </div>
            
            {/* Search Input */}
            <div className="mb-3">
              <div>
                <label htmlFor="search-players" className="text-sm font-medium text-neutral-700 mb-1 block">
                  Search Players
                </label>
                <div className="relative">
                  <Input
                    id="search-players"
                    type="text"
                    placeholder="Search by name, email, position, club, nationality..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="fas fa-search text-neutral-400"></i>
                  </div>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Clear search"
                    >
                      <i className="fas fa-times text-sm"></i>
                    </button>
                  )}
                </div>
                
                {/* Search results count */}
                {searchTerm && (
                  <div className="mt-1 text-xs text-gray-500">
                    {filteredPlayers.length} player{filteredPlayers.length !== 1 ? 's' : ''} found
                    {filteredPlayers.length !== players?.length && (
                      <span className="ml-1">
                        (out of {players?.length || 0} total)
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Player List */}
            <div className="space-y-2 lg:space-y-3 flex-1 overflow-y-auto max-h-[600px]">
              {filteredPlayers.map((player) => (
                <div
                  key={player.id}
                  className={`flex items-center space-x-2 lg:space-x-3 p-2 rounded-lg shadow-sm border transition-all ${
                    isPlayerEmailAdded(player)
                      ? 'bg-green-50 border-green-200'
                      : selectedPlayer?.id === player.id 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-white border-transparent hover:bg-gray-50'
                  }`}
                >
                  <AvatarPlaceholder 
                    name={player.name} 
                    className="w-6 h-6 lg:w-8 lg:h-8 flex-shrink-0"
                  />
                  <div 
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => handlePlayerClick(player)}
                  >
                    <p className="text-xs lg:text-sm font-medium text-gray-900 truncate">{player.name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {(player.position && Array.isArray(player.position)) ? player.position.join(', ') : 'Position'} • {player.age || 'Age'}y
                    </p>
                    <p className={`text-xs truncate ${player.email ? 'text-gray-400' : 'text-red-400'}`}>
                      {player.email || 'No email available'}
                    </p>
                  </div>
                  <div className="flex flex-col space-y-1">
                    {/* Variable Selection Button */}
                    <button
                      onClick={() => handlePlayerClick(player)}
                      className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                        selectedPlayer?.id === player.id 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-200 hover:bg-blue-200'
                      }`}
                      title="Select for variable mapping"
                    >
                      <i className={`text-xs ${
                        selectedPlayer?.id === player.id ? 'fas fa-user-check' : 'fas fa-user'
                      }`}></i>
                    </button>
                    
                    {/* Email Addition Button */}
                    {player.email && (
                      <button
                        onClick={() => isPlayerEmailAdded(player) ? removePlayerEmail(player) : addPlayerEmail(player)}
                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                          isPlayerEmailAdded(player)
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : previouslySentEmails.has(player.email.toLowerCase().trim())
                            ? 'bg-orange-200 hover:bg-orange-300'
                            : 'bg-gray-200 hover:bg-green-200'
                        }`}
                        title={
                          isPlayerEmailAdded(player) 
                            ? 'Remove from recipients' 
                            : previouslySentEmails.has(player.email.toLowerCase().trim())
                            ? 'Add to recipients (previously emailed)'
                            : 'Add to recipients'
                        }
                      >
                        <i className={`text-xs ${
                          isPlayerEmailAdded(player) 
                            ? 'fas fa-envelope-open' 
                            : previouslySentEmails.has(player.email.toLowerCase().trim())
                            ? 'fas fa-redo'
                            : 'fas fa-envelope'
                        }`}></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              {searchTerm && filteredPlayers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-search text-3xl mb-3 text-gray-300"></i>
                  <p className="text-sm font-medium mb-1">No players found</p>
                  <p className="text-xs">
                    No players match "{searchTerm}"
                  </p>
                  <button
                    onClick={() => setSearchTerm("")}
                    className="mt-3 text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear search and show all players
                  </button>
                </div>
              )}
            </div>
            
            {/* Helper text */}
            <div className="mt-auto pt-3 border-t">
              <div className="text-xs text-gray-500 space-y-1">
                <div className="flex items-center">
                  <i className="fas fa-lightbulb mr-1"></i>
                  <span>
                    {formData.selectedPlayerData ? (
                      <span className="text-blue-600">
                        <strong>{formData.selectedPlayerData.name}</strong> selected for variables
                      </span>
                    ) : (
                      "Click name to select for variable mapping"
                    )}
                  </span>
                </div>
                
                <div className="flex items-center">
                  <i className="fas fa-envelope mr-1"></i>
                  <span>
                    {formData.selectedPlayerIds && formData.selectedPlayerIds.length > 0 ? (
                      <span className="text-green-600">
                        <strong>{formData.selectedPlayerIds.length}</strong> player{formData.selectedPlayerIds.length !== 1 ? 's' : ''}
                        {formData.emailTags && formData.selectedPlayerIds.length !== formData.emailTags.length && (
                          <span className="text-gray-500">
                            {' '}({formData.emailTags.length} unique email{formData.emailTags.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                    ) : (
                      "Click envelope to add players"
                    )}
                  </span>
                </div>
              </div>
              
              {formData.selectedPlayerData && (
                <button
                  onClick={() => {
                    setSelectedPlayer(null);
                    setFormData(prev => ({
                      ...prev,
                      selectedPlayerData: null,
                    }));
                  }}
                  className="mt-2 text-xs text-red-600 hover:text-red-800 flex items-center"
                >
                  <i className="fas fa-times mr-1"></i>
                  Clear variable selection
                </button>
              )}
            </div>
          </div>

          {/* Enhanced Email Signature */}
          <div className="bg-white p-3 lg:p-4 rounded-lg shadow-sm border border-neutral-200 min-h-[150px] flex flex-col">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <i className="fas fa-signature text-sm"></i>
              <span className="text-sm lg:text-base">Email Signature</span>
            </h3>
            
            {/* Current Signature Display */}
            <div className="text-xs lg:text-sm text-gray-600 space-y-1 mb-4">
              <p className="font-medium">{signature.name}</p>
              {signature.title && <p>{signature.title}</p>}
              {signature.organization && <p>{signature.organization}</p>}
              {signature.department && <p>{signature.department}</p>}
              <p className="text-blue-600 break-all">{signature.email}</p>
              {signature.phone && <p>{signature.phone}</p>}
              {signature.other.filter(field => field.trim()).map((field, index) => (
                <p key={index} className="text-xs">{field}</p>
              ))}
            </div>
            
            {/* Signature Preview */}
            <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200 flex-1">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Email Preview</h4>
              <div className="text-xs text-gray-600 whitespace-pre-line font-mono">
                {generateSignatureText(signature)}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowEditSignature(true)}
                  className="text-xs px-3 py-1 h-7 flex-1"
                >
                  <i className="fas fa-edit text-xs mr-1"></i>
                  Edit
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={addSignatureToContent}
                  className="text-xs px-3 py-1 h-7 flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <i className="fas fa-plus text-xs mr-1"></i>
                  Add to Email
                </Button>
              </div>
              
              {/* Quick Signature Templates */}
              <div className="pt-2 border-t">
                <p className="text-xs text-gray-500 mb-2">Quick Templates:</p>
                <div className="space-y-1">
                  <button 
                    onClick={() => {
                      const basicSig = "\n\nBest regards,\n" + signature.name;
                      setFormData(prev => ({ ...prev, content: prev.content + basicSig }));
                      toast({
                        title: "Template added",
                        description: "Basic signature template added to email.",
                      });
                    }}
                    className="w-full text-left px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Basic: "Best regards, {signature.name}"
                  </button>
                  <button 
                    onClick={() => {
                      const formalSig = "\n\nSincerely,\n" + signature.name + (signature.title ? "\n" + signature.title : "");
                      setFormData(prev => ({ ...prev, content: prev.content + formalSig }));
                      toast({
                        title: "Template added",
                        description: "Formal signature template added to email.",
                      });
                    }}
                    className="w-full text-left px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Formal: "Sincerely, {signature.name}"
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Variable Helper */}
          <div className="bg-white p-3 lg:p-4 rounded-lg shadow-sm border border-neutral-200 md:col-span-2 xl:col-span-1 min-h-[150px] flex flex-col">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
              <i className="fas fa-code text-sm"></i>
              <span className="text-sm lg:text-base">Variables</span>
              {formData.selectedPlayerData && (
                <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                  {formData.selectedPlayerData.name}
                </span>
              )}
            </h3>
            
            <div className="grid grid-cols-1 gap-2 text-xs lg:text-sm flex-1 overflow-y-auto">
              {([
                { 
                  label: "Player Name (Full)", 
                  variable: "PlayerName",
                  value: formData.selectedPlayerData?.name || "Player Name"
                },
                { 
                  label: "First Name", 
                  variable: "PlayerFirstName",
                  value: (() => {
                    const { firstName } = splitPlayerName(formData.selectedPlayerData?.name || '');
                    return firstName || "First Name";
                  })()
                },
                { 
                  label: "Last Name", 
                  variable: "PlayerLastName", 
                  value: (() => {
                    const { lastName } = splitPlayerName(formData.selectedPlayerData?.name || '');
                    return lastName || "Last Name";
                  })()
                },
                { 
                  label: "General Position", 
                  variable: "PlayerPosition",
                  value: (formData.selectedPlayerData?.position && Array.isArray(formData.selectedPlayerData.position)) ? formData.selectedPlayerData.position.join(', ') : "Position"
                },
                { 
                  label: "Current Club", 
                  variable: "PlayerClub",
                  value: formData.selectedPlayerData?.current_club || "Current Club"
                },
                { 
                  label: "Playing Level", 
                  variable: "PlayerLevel",
                  value: formData.selectedPlayerData?.playing_level || "Playing Level"
                },
              ] as const).map(({ label, variable, value }) => (
                <div key={variable} className="flex justify-between items-center py-1">
                  <div className="flex flex-col flex-1 min-w-0 mr-2">
                    <span className="text-gray-600 text-xs">{label}</span>
                    <span className={`text-xs truncate ${formData.selectedPlayerData ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                      {value}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => insertVariable(variable)}
                    className="flex-shrink-0 h-8 px-2"
                  >
                    <code className="bg-gray-200 px-1 py-0.5 rounded text-xs">
                      {`{{${variable}}}`}
                    </code>
                  </Button>
                </div>
              ))}
            </div>
            
            <div className="mt-auto pt-3 border-t">
              {!formData.selectedPlayerData && (
                <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                  <i className="fas fa-info-circle mr-1"></i>
                  Select a player to see their actual values
                </div>
              )}
              {formData.selectedPlayerData && formData.content.includes('{{') && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                  <i className="fas fa-check-circle mr-1"></i>
                  Variables will be replaced with {formData.selectedPlayerData.name}'s data
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Player Info Banner */}
      {formData.selectedPlayerData && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-blue-800 font-medium">
              Variables from: {formData.selectedPlayerData.name}
            </span>
            {formData.selectedPlayerData.position && Array.isArray(formData.selectedPlayerData.position) && (
              <span className="text-blue-600">• {formData.selectedPlayerData.position.join(', ')}</span>
            )}
            {formData.selectedPlayerData.age && (
              <span className="text-blue-600">• {formData.selectedPlayerData.age}y</span>
            )}
          </div>
        </div>
      )}

      {/* Signature Modal */}
      <SignatureModal
        isOpen={showEditSignature}
        onClose={() => setShowEditSignature(false)}
        signature={signature}
        onSignatureUpdate={handleSignatureUpdate}
      />

      {/* Centralized Warning Management */}
      <WarningManager
        ref={warningManagerRef}
        onCheckGraduationWarnings={checkGraduationWarnings}
        onCheckDuplicateEmails={checkDuplicateEmails}
        onValidateMissingVariables={(content: string, subject: string, playerList: any[]) => {
          return validateVariablesForPlayers(content, subject, playerList as Player[]);
        }}
        onSendEmail={(emailData) => sendEmailMutation.mutate(emailData)}
        players={(players?.filter(p => p.email) || []) as any[]}
      />
    </div>
  );
}